import { Badge } from '@mantine/core';
import { IconClock2 } from '@tabler/icons-react';
import { memo } from 'react';
import cardClasses from '~/components/Cards/Cards.module.css';
import { AddArtFrameMenuItem } from '~/components/Decorations/AddArtFrameMenuItem';
import { EdgeMedia2 } from '~/components/EdgeMedia/EdgeMedia';
import { getSkipValue } from '~/components/EdgeMedia/EdgeMedia.util';
import { ImageContextMenu } from '~/components/Image/ContextMenu/ImageContextMenu';
import { OnsiteIndicator } from '~/components/Image/Indicators/OnsiteIndicator';
import { ImageGuard2 } from '~/components/ImageGuard/ImageGuard2';
import { MediaHash } from '~/components/ImageHash/ImageHash';
import { NextLink } from '~/components/NextLink/NextLink';
import { DaysFromNow } from '~/components/Dates/DaysFromNow';
import { PostReactions } from '~/components/Reaction/Reactions';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import type { PostsInfiniteModel } from '~/server/services/post.service';
import { CosmeticEntity } from '~/shared/utils/prisma/enums';
import classes from './PostsCard.module.css';
import { TwCard } from '~/components/TwCard/TwCard';
import { TwCosmeticWrapper } from '~/components/TwCosmeticWrapper/TwCosmeticWrapper';
import { useTrackImpression } from '~/components/TrackView/useTrackImpression';

export function PostsCard({
  data: { images, id, stats, imageCount, cosmetic, user, publishedAt },
  height,
}: {
  data: PostsInfiniteModel;
  height?: number;
}) {
  const currentUser = useCurrentUser();
  const image = images[0];
  const isOwner = currentUser?.id === user.id;
  const scheduledAt = publishedAt && new Date(publishedAt) > new Date() ? publishedAt : null;
  const impressionRef = useTrackImpression<HTMLElement>([
    { entityType: 'Post', entityId: id },
    ...(image ? [{ entityType: 'Image' as const, entityId: image.id }] : []),
  ]);
  return (
    <TwCosmeticWrapper cosmetic={cosmetic?.data} style={cosmetic?.data ? { height } : undefined}>
      <TwCard
        ref={impressionRef}
        className="border shadow"
        style={!cosmetic?.data ? { height } : undefined}
      >
        <ImageGuard2 image={image} connectType="post" connectId={id}>
          {(safe) => (
            <>
              {image.onSite && <OnsiteIndicator isRemix={!!image.remixOfId} />}

              <ImageGuard2.BlurToggle className="absolute left-2 top-2 z-10" />
              {safe && (
                <div className="absolute right-2 top-2 z-10">
                  <div className="flex flex-col items-center gap-2">
                    <ImageContextMenu
                      image={image}
                      context="post"
                      postImageCount={imageCount}
                      className=""
                      additionalMenuItems={
                        isOwner ? (
                          <AddArtFrameMenuItem
                            entityType={CosmeticEntity.Post}
                            entityId={id}
                            image={image}
                            currentCosmetic={cosmetic}
                          />
                        ) : null
                      }
                    />
                  </div>
                </div>
              )}

              {/* The grid sizes the card, so the media has to fill it: without flex-1 this link is
                  content-height and `.image`'s height: 100% resolves against auto. */}
              <NextLink href={`/posts/${id}`} className="min-h-0 flex-1">
                {!safe ? (
                  <div style={{ aspectRatio: (image?.width ?? 1) / (image?.height ?? 1) }}>
                    <MediaHash {...image} />
                  </div>
                ) : (
                  <EdgeMedia2
                    metadata={image.metadata}
                    src={image.url}
                    className={cardClasses.image}
                    name={image.name ?? image.id.toString()}
                    alt={image.name ?? undefined}
                    skip={getSkipValue(image)}
                    type={image.type}
                    imageId={image.id}
                    width={450}
                    placeholder="empty"
                  />
                )}
              </NextLink>
              {(scheduledAt || !publishedAt) && (
                <Badge
                  className={classes.publishState}
                  color={scheduledAt ? 'blue' : 'gray'}
                  variant="filled"
                  radius="sm"
                  leftSection={<IconClock2 size={12} strokeWidth={2.5} />}
                >
                  {scheduledAt ? <DaysFromNow date={scheduledAt} live /> : 'Draft'}
                </Badge>
              )}
              <PostReactions
                className={classes.reactions}
                imageCount={imageCount}
                metrics={{
                  likeCount: stats?.likeCount,
                  dislikeCount: stats?.dislikeCount,
                  heartCount: stats?.heartCount,
                  laughCount: stats?.laughCount,
                  cryCount: stats?.cryCount,
                }}
              />
            </>
          )}
        </ImageGuard2>
      </TwCard>
    </TwCosmeticWrapper>
  );
}

export const PostsCardMemoized = memo(PostsCard);
